import React, { useState, useEffect } from 'react';
import { 
  IconSearch, IconEdit, IconTrash, IconUserPlus, 
  IconSortAscending, IconSortDescending
} from '@tabler/icons-react';
import { adminService } from '../../services/adminApi';
import styles from '../../pages/AdminDashboard.module.css';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

interface UserManagementProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onSuccess, onError }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Récupérer les utilisateurs depuis l'API
        const response = await adminService.getAllUsers();
        
        // Vérifier la structure des données et l'afficher pour le débogage
        console.log('Structure de la réponse API:', response);
        
        // S'assurer que usersData est un tableau
        let usersData: any[] = [];
        
        if (Array.isArray(response)) {
          usersData = response;
        } else if (response && typeof response === 'object') {
          const responseObj = response as Record<string, any>;
          if (responseObj.users && Array.isArray(responseObj.users)) {
            usersData = responseObj.users;
          } else if (responseObj.data && Array.isArray(responseObj.data)) {
            usersData = responseObj.data;
          }
        }
        
        if (!Array.isArray(usersData)) {
          console.error('La réponse API n\'est pas un tableau:', usersData);
          throw new Error('Format de données utilisateur inattendu');
        }
        
        // Formater les données pour correspondre à notre interface User
        const formattedUsers = usersData.map(user => ({
          id: user.userId || user.id, // Support pour userId ou id
          name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          email: user.email,
          role: user.role || user.userType || 'user', // Plusieurs sources possibles pour le rôle
          isActive: user.isActive !== false, // Par défaut, considérer comme actif
          createdAt: user.createdAt || new Date().toISOString()
        }));
        
        setUsers(formattedUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Impossible de récupérer les utilisateurs. Veuillez réessayer plus tard.');
        if (onError) onError('Impossible de récupérer les utilisateurs. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [onError]);

  const handleEdit = (user: User) => {
    setCurrentUser(user);
    setEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.")) {
      try {
        setError(null);
        // Appel à l'API pour supprimer l'utilisateur
        await adminService.deleteUser(id);
        
        // Mise à jour de l'état local
        setUsers(users.filter(user => user.id !== id));
        if (onSuccess) onSuccess('Utilisateur supprimé avec succès.');
      } catch (error) {
        console.error('Error deleting user:', error);
        setError('Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer plus tard.');
        if (onError) onError('Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer plus tard.');
      }
    }
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      setError(null);
      let updatedUser;
      
      // Adapter le format pour l'API
      const userDataForApi = {
        ...currentUser,
        userId: currentUser.id, // S'assurer que le backend reçoit userId
      };
      
      if (currentUser.id) {
        // Mise à jour d'un utilisateur existant
        updatedUser = await adminService.updateUser(currentUser.id, userDataForApi);
        
        // S'assurer que la réponse est au format attendu par le frontend
        const formattedUser = {
          id: updatedUser.userId || updatedUser.id,
          name: updatedUser.name,
          email: updatedUser.email,
          role: updatedUser.role || 'user',
          isActive: updatedUser.isActive !== false,
          createdAt: updatedUser.createdAt || currentUser.createdAt
        };
        
        // Mettre à jour l'état local
        setUsers(users.map(user => 
          user.id === currentUser.id ? formattedUser : user
        ));
        if (onSuccess) onSuccess('Utilisateur mis à jour avec succès.');
      } else {
        // Création d'un nouvel utilisateur
        updatedUser = await adminService.createUser(userDataForApi);
        
        // S'assurer que la réponse est au format attendu par le frontend
        const formattedUser = {
          id: updatedUser.userId || updatedUser.id,
          name: updatedUser.name,
          email: updatedUser.email,
          role: updatedUser.role || 'user',
          isActive: updatedUser.isActive !== false,
          createdAt: updatedUser.createdAt || new Date().toISOString()
        };
        
        // Mettre à jour l'état local
        setUsers([...users, formattedUser]);
        if (onSuccess) onSuccess('Utilisateur créé avec succès.');
      }
      
      // Fermer le modal et réinitialiser l'utilisateur courant
      setEditModalOpen(false);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Erreur lors de l\'enregistrement de l\'utilisateur. Veuillez réessayer plus tard.');
      if (onError) onError('Erreur lors de l\'enregistrement de l\'utilisateur. Veuillez réessayer plus tard.');
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(user => 
      (user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
       user.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (roleFilter === 'all' || user.role === roleFilter)
    )
    .sort((a, b) => {
      if (sortField === 'name') {
        return sortDirection === 'asc' 
          ? a.name.localeCompare(b.name) 
          : b.name.localeCompare(a.name);
      } else if (sortField === 'email') {
        return sortDirection === 'asc' 
          ? a.email.localeCompare(b.email) 
          : b.email.localeCompare(a.email);
      } else if (sortField === 'createdAt') {
        return sortDirection === 'asc' 
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() 
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return 0;
    });

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const roleOptions = [
    { value: 'user', label: 'Utilisateur' },
    { value: 'admin', label: 'Administrateur' },
    { value: 'organization', label: 'Organisation' }
  ];

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2>Gestion des Utilisateurs</h2>
        <button 
          className={styles.addButton}
          onClick={() => {
            setCurrentUser({
              id: 0,
              name: '',
              email: '',
              role: 'user',
              isActive: true,
              createdAt: new Date().toISOString()
            });
            setEditModalOpen(true);
          }}
        >
          <IconUserPlus size={16} />
          <span>Ajouter un utilisateur</span>
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <div className={styles.filterContainer}>
        <div className={styles.searchBar}>
          <IconSearch size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Rechercher par nom ou email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <select
          className={styles.select}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">Tous les rôles</option>
          {roleOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Chargement des utilisateurs...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <p className={styles.emptyMessage}>Aucun utilisateur trouvé.</p>
      ) : (
        <table className={styles.dataGrid}>
          <thead>
            <tr>
              <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>
                Nom {sortField === 'name' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th onClick={() => toggleSort('email')} style={{ cursor: 'pointer' }}>
                Email {sortField === 'email' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th>Rôle</th>
              <th onClick={() => toggleSort('createdAt')} style={{ cursor: 'pointer' }}>
                Date d'inscription {sortField === 'createdAt' && (
                  sortDirection === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />
                )}
              </th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`${styles.badge} ${
                    user.role === 'admin' ? styles.badgeRed : 
                    user.role === 'organization' ? styles.badgeBlue : 
                    styles.badgeGray
                  }`}>
                    {user.role === 'admin' ? 'Administrateur' : 
                     user.role === 'organization' ? 'Organisation' : 
                     'Utilisateur'}
                  </span>
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString('fr-FR')}</td>
                <td>
                  <span className={`${styles.badge} ${user.isActive ? styles.badgeGreen : styles.badgeRed}`}>
                    {user.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td>
                  <button 
                    className={styles.actionButton}
                    onClick={() => handleEdit(user)}
                  >
                    <IconEdit size={16} />
                  </button>
                  <button 
                    className={styles.actionButton}
                    onClick={() => handleDelete(user.id)}
                  >
                    <IconTrash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal for editing/creating user */}
      {editModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {currentUser?.id ? 'Modifier l\'utilisateur' : 'Ajouter un nouvel utilisateur'}
              </h3>
              <button 
                className={styles.closeButton}
                onClick={() => setEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            
            {currentUser && (
              <form onSubmit={handleSaveUser} className={styles.modalForm}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nom</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    required
                    value={currentUser.name}
                    onChange={(e) => setCurrentUser({...currentUser, name: e.target.value})}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email</label>
                  <input
                    type="email"
                    className={styles.formInput}
                    required
                    value={currentUser.email}
                    onChange={(e) => setCurrentUser({...currentUser, email: e.target.value})}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Rôle</label>
                  <select
                    className={styles.formSelect}
                    required
                    value={currentUser.role}
                    onChange={(e) => setCurrentUser({...currentUser, role: e.target.value})}
                  >
                    {roleOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <input
                      type="checkbox"
                      checked={currentUser.isActive}
                      onChange={(e) => setCurrentUser({...currentUser, isActive: e.target.checked})}
                    />
                    {' '}Utilisateur actif
                  </label>
                </div>
                
                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setEditModalOpen(false)}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={styles.saveButton}
                  >
                    Enregistrer
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
